import React, { useContext, useEffect } from "react";
import ComponentsService from "../../services/ComponentsService";
import {
  ComponentTemplateDto,
  ComponentTemplatePrivacyType,
  ComponentTemplateSort,
} from "@growth-machine-llc/stridist-api-client";
import { useTemplateListFilterContext } from "../../contexts/TemplateLibraryFilterContext";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";

import { EditorProgramContext } from "../new-editor/hooks";
import SidebarTemplate, {
  TEMPLATE_SIDEBAR_WIDTH,
} from "./sidebar/SidebarTemplate";
import { useTemplateSidebar } from "../../contexts/TemplateSidebarContext";
import { useComponentTemplateDialog } from "../../contexts/TemplateDialogContext";
import { JourneyAnchors } from "../../constants";
import { JourneyRunContext } from "../../contexts/JourneyRunContext";
import {
  ComponentTemplateLibraryCardSkeleton,
  ComponentTemplateLibraryTableSkeleton,
} from "../loading/ComponentTemplateLibrarySkeleton";
import ComponentTemplateRow from "./ComponentTemplateRow";
import { useQuery } from "@tanstack/react-query";
import { CategoryFilter } from "./CategoryFilter";
import { ComponentTemplateCardView } from "./ComponentTemplateCardView";
import { getCommonTransition } from "../../utils/mui";
import { ComponentNameSortButton } from "./ComponentNameSortButton";

const useStyles = makeStyles((theme) => ({
  root: {
    borderRadius: theme.spacing(1.5),
    padding: 1,
    boxShadow: theme.shadows[4],
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down("md")]: {
      boxShadow: "none",
    },
  },

  container: {
    margin: 0,
    containerType: "inline-size",

    [theme.breakpoints.up("md")]: {
      margin: theme.spacing(1, 2),
    },
  },

  table: {
    "& th": {
      fontWeight: "bold",
      fontSize: 15,
    },

    "& td": {
      fontWeight: 500,
      fontSize: 14,
    },

    "& tfoot td": {
      borderBottom: "none",
    },
  },
}));

export interface ComponentTemplateLibraryProps {}

export const getComponentTemplateLibraryWidths = () => {
  return {
    thumbnail: "16%",
    title: "54%",
    category: "20%",
    type: "5%",
    actions: "5%",
  };
};

const DEFAULT_ROWS_PER_PAGE = 10;
const DEFAULT_PAGE = 1;

export const COMPONENT_TEMPLATES_LIBRARY_QUERY_KEY =
  "component-templates-library";
6;
const ComponentTemplateLibrary = (props: ComponentTemplateLibraryProps) => {
  const { isOpen: isSidebarOpen, openSidebar } = useTemplateSidebar();

  const { openDialog } = useComponentTemplateDialog();
  const { isRunning } = useContext(JourneyRunContext);

  const columnWidths = getComponentTemplateLibraryWidths();

  const theme = useTheme();
  const downSlg = useMediaQuery(theme.breakpoints.down("slg"));
  const downMd = useMediaQuery(theme.breakpoints.down("md"));

  const [page, setPage] = React.useState(DEFAULT_PAGE);
  const [rowsPerPage, setRowsPerPage] = React.useState(DEFAULT_ROWS_PER_PAGE);

  const [sort, setSort] = React.useState<ComponentTemplateSort | null>(null);

  const s = useStyles();
  const [selectedTemplate, setSelectedTemplate] =
    React.useState<ComponentTemplateDto>(null);

  const {
    query,
    sortKey,
    templatePrivacyType,
    templateType: templateTypeContext,
    handleComponentTemplateTypeChange,
  } = useTemplateListFilterContext();

  const componentType =
    templateTypeContext === "ALL" ? null : templateTypeContext;

  const { data: componentTemplatesData, isFetching } = useQuery({
    queryKey: [
      COMPONENT_TEMPLATES_LIBRARY_QUERY_KEY,
      { componentType, query, templatePrivacyType, page, rowsPerPage, sort },
    ],
    queryFn: () =>
      ComponentsService.getLibraryComponentTemplates(
        componentType,
        query,
        templatePrivacyType,
        page,
        rowsPerPage,
        sort,
      ),
  });

  const templates = componentTemplatesData?.items || [];

  React.useEffect(() => {
    if (templates?.length > 0) {
      setSelectedTemplate(templates[0]);
      return;
    }
  }, [templates]);

  const handleTemplateClick = React.useCallback(
    (
      event: React.MouseEvent<HTMLDivElement, MouseEvent>,
      editMode?: boolean,
    ) => {
      const {
        dataset: { templateId },
      } = event.currentTarget;
      event.stopPropagation();
      event.preventDefault();

      const template = templates?.find((it) => it.id === templateId);
      setSelectedTemplate(template);
      if (!downSlg) {
        openSidebar();
      } else {
        openDialog(template);
      }
    },
    [templates, setSelectedTemplate, selectedTemplate, downSlg],
  );

  const handleOpenDialog = React.useCallback(
    (template: ComponentTemplateDto, editMode?: boolean) => {
      openDialog(template, editMode);
    },
    [],
  );

  useEffect(() => {
    if (isRunning && selectedTemplate && !downMd) {
      openSidebar();
    }
  }, [isRunning, selectedTemplate, openSidebar, downMd]);

  const handleChangePage = (_, newPage) => {
    const newPageNumber = newPage + 1;
    setPage(newPageNumber);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(event.target.value);
    setPage(1);
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        position: "relative",
      }}
    >
      <Box
        sx={{
          width: "100%",
          display: "flex",
          position: "absolute",
          marginInline: "auto",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            display: "flex",
            position: "relative",
            gap: isSidebarOpen ? 4 : 0,
          }}
        >
          <Box
            sx={{
              marginBottom: 2,
              flexGrow: 1,

              zIndex: theme.zIndex.appBar,
              width: !isSidebarOpen
                ? "100%"
                : `calc(100% - ${TEMPLATE_SIDEBAR_WIDTH}px)`,
              mr: downSlg
                ? "auto"
                : !isSidebarOpen
                  ? `-${TEMPLATE_SIDEBAR_WIDTH}px`
                  : 0,
              transition: getCommonTransition(theme, "margin-right"),
            }}
          >
            <Paper className={s.root}>
              <Box className={s.container}>
                <Table className={s.table} aria-label="exercise table">
                  {!downMd ? (
                    <TableHead>
                      <TableRow>
                        <TableCell width={columnWidths.thumbnail}>
                          Thumbnail
                        </TableCell>
                        <TableCell width={columnWidths.title}>
                          <ComponentNameSortButton
                            sort={sort}
                            onChangeSort={(nextSort) => {
                              setSort(nextSort);
                              setPage(1);
                            }}
                          />
                        </TableCell>
                        <TableCell align="center" sx={{ width: "18%" }}>
                          <CategoryFilter
                            onChange={handleComponentTemplateTypeChange}
                            contextFilters={componentType}
                          />
                        </TableCell>
                        <TableCell align="center" width={columnWidths.type}>
                          Access
                        </TableCell>
                        <TableCell align="center" width={columnWidths.actions}>
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                  ) : (
                    <Box
                      display={"flex"}
                      justifyContent={"space-between"}
                      alignItems={"center"}
                      width={"100%"}
                      flexWrap={"wrap"}
                    >
                      <CategoryFilter
                        onChange={handleComponentTemplateTypeChange}
                        contextFilters={componentType}
                      />
                    </Box>
                  )}
                  {isFetching ? (
                    Array.from({ length: rowsPerPage }).map((_, index) => (
                      <React.Fragment key={index}>
                        {downMd ? (
                          <ComponentTemplateLibraryCardSkeleton />
                        ) : (
                          <ComponentTemplateLibraryTableSkeleton
                            className={s.table}
                          />
                        )}
                      </React.Fragment>
                    ))
                  ) : (
                    <>
                      <TableBody>
                        {templates.length > 0 ? (
                          templates.map((item, index) => (
                            <React.Fragment key={item.id}>
                              {downMd ? (
                                <ComponentTemplateCardView
                                  template={item}
                                  onContentClick={handleTemplateClick}
                                  onEdit={() => handleOpenDialog(item, true)}
                                />
                              ) : (
                                <ComponentTemplateRow
                                  type={item.type}
                                  className={
                                    index === 0 &&
                                    JourneyAnchors.FIRST_COMPONENT_TEMPLATE
                                  }
                                  component={item}
                                  selected={
                                    selectedTemplate?.id === item.id &&
                                    isSidebarOpen
                                  }
                                  onContentClick={handleTemplateClick}
                                  onContentDoubleClick={() => {
                                    setSelectedTemplate(item);
                                    handleOpenDialog(item);
                                  }}
                                  data-template-id={item.id}
                                  onEdit={() => handleOpenDialog(item, true)}
                                />
                              )}
                            </React.Fragment>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell align="center" colSpan={5}>
                              {"No components found for current filters."}
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </>
                  )}
                  <TableFooter>
                    <TableRow>
                      <TablePagination
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        colSpan={9}
                        count={componentTemplatesData?.totalCount || 0}
                        rowsPerPage={rowsPerPage}
                        page={Number(page - 1)}
                        showFirstButton
                        showLastButton
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                      />
                    </TableRow>
                  </TableFooter>
                </Table>
              </Box>
            </Paper>
          </Box>
          {!downSlg && (
            <EditorProgramContext.Provider value={{ programId: 0 }}>
              <SidebarTemplate
                content={selectedTemplate}
                type={selectedTemplate?.type}
              />
            </EditorProgramContext.Provider>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ComponentTemplateLibrary;
